import { useState, type FormEvent } from 'react';
import { Mail, Phone, UserRound, X } from 'lucide-react';
import type { OwnerProfile } from '../lib/api';
import './OwnerProfileDialog.css';

type ProfileForm = { name: string; phone: string };

export default function OwnerProfileDialog({ profile, required, onClose, onSave }: {
  profile: OwnerProfile;
  required: boolean;
  onClose: () => void;
  onSave: (values: ProfileForm) => Promise<void>;
}) {
  const [form, setForm] = useState<ProfileForm>({ name: profile.name, phone: profile.phone || '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email) && !profile.email.endsWith('@auth0.local');

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await onSave({ name: form.name.trim(), phone: form.phone.trim() });
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Unable to save your profile');
    } finally {
      setSaving(false);
    }
  };

  return <div className="modal-backdrop owner-profile-backdrop" role="presentation" onMouseDown={(event) => { if (!required && event.target === event.currentTarget) onClose(); }}>
    <section className="modal owner-profile-modal" role="dialog" aria-modal="true" aria-labelledby="owner-profile-title">
      <div className="modal-heading">
        <div><p className="eyebrow">Your account</p><h2 id="owner-profile-title">{required ? 'Complete your profile' : 'Edit profile'}</h2></div>
        {!required && <button type="button" className="icon-button" onClick={onClose} aria-label="Close profile editor"><X size={19} /></button>}
      </div>
      {required && <p className="owner-profile-intro">Add your name to finish setting up your account. Your sign-in email is managed securely by Auth0.</p>}
      <form onSubmit={(event) => void submit(event)}>
        <label className="owner-profile-field"><span><UserRound size={14} /> Name</span><input autoFocus type="text" maxLength={160} autoComplete="name" required value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} /></label>
        <label className="owner-profile-field"><span><Mail size={14} /> Email</span><input type="email" autoComplete="email" required readOnly value={profile.email} aria-describedby="owner-profile-email-note" /></label>
        <small className="owner-profile-email-note" id="owner-profile-email-note">To change your sign-in email, update it through your Auth0 account.</small>
        <label className="owner-profile-field"><span><Phone size={14} /> Phone <small>Optional</small></span><input type="tel" maxLength={40} autoComplete="tel" value={form.phone} onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))} /></label>
        {!validEmail && <p className="form-error" role="alert">A valid sign-in email is required. Add and verify an email with Auth0 to continue.</p>}
        {error && <p className="form-error" role="alert">{error}</p>}
        <div className="modal-actions">
          {!required && <button type="button" className="button button-secondary" onClick={onClose}>Cancel</button>}
          <button type="submit" className="button button-primary" disabled={saving || !validEmail || !form.name.trim()}>{saving ? 'Saving...' : required ? 'Complete setup' : 'Save profile'}</button>
        </div>
      </form>
    </section>
  </div>;
}
